import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/no-danger */
/* eslint-disable react/destructuring-assignment */
import { useMemo } from 'react';
import { Box, Skeleton } from '@mui/material';
import Carousel from 'react-multi-carousel';
// import 'react-multi-carousel/lib/styles.css';
import { useStore, } from '@mcn-platform/models';
import { observer } from 'mobx-react-lite';
import getData from './getData';
import PlaceHolder from '../common/PlaceHolder';
import WysiwygTitle from './WysiwygTitle';
import VerticalMediaCard from './card/VerticalMediaCard';
export const responsive = {
    superLargeDesktop: {
        // the naming can be any, depends on you.
        breakpoint: { max: 4000, min: 1536 },
        items: 4,
    },
    largeDesktop: {
        breakpoint: { max: 1536, min: 1200 },
        items: 4,
    },
    desktop: {
        breakpoint: { max: 1200, min: 900 },
        items: 4,
    },
    tablet: {
        breakpoint: { max: 900, min: 600 },
        items: 3,
    },
    mobile: {
        breakpoint: { max: 600, min: 0 },
        items: 1,
    },
};
const CarouselSkeleton = () => (_jsx(Box, { sx: {
        display: 'flex',
        gap: 2,
        overflow: 'hidden',
        '& > *': {
            flexShrink: 0,
        },
    }, children: [...Array(4).keys()].map((i) => (_jsxs(Box, { sx: {
            minWidth: { xs: '100%', sm: '280px' },
            maxWidth: { xs: '100%', sm: '280px' },
            margin: '4px 8px',
        }, children: [_jsx(Skeleton, { variant: "rectangular", width: "100%", height: 200, sx: { mb: 1 } }), _jsx(Skeleton, { variant: "text", width: "80%", height: 24, sx: { mb: 0.5 } }), _jsx(Skeleton, { variant: "text", width: "60%", height: 20, sx: { mb: 1 } }), _jsx(Skeleton, { variant: "text", width: "100%", height: 16 }), _jsx(Skeleton, { variant: "text", width: "90%", height: 16 })] }, i))) }));
const ItemCarousel = ({ categoryId, items, content, showPrice = true, showTitle = true, cardShowTitle = true, cardShowImage = true, cardShowInfoBar = true, cardShowInstructor = true, cardShowDescription = true, cardMaxLinesDescription = 4, editData, cardShowActionButton = true, maxItemCount, padding, backgroundColor, configId, limitToPageWidth, disabled = false, }) => {
    const store = useStore();
    const cardConfig = useMemo(() => ({
        courseCardShowPrice: showPrice,
        courseCardShowTitle: cardShowTitle,
        courseCardShowImage: cardShowImage,
        courseCardShowInstructor: cardShowInstructor || false,
        courseCardMaxLinesDescription: cardMaxLinesDescription,
        courseCardShowDesc: cardShowDescription,
        courseCardShowInfoBar: cardShowInfoBar,
        courseCardShowActionButton: cardShowActionButton,
    }), [
        showPrice,
        cardShowDescription,
        cardShowTitle,
        cardShowImage,
        cardShowInfoBar,
        cardShowInstructor,
        cardMaxLinesDescription,
        cardShowActionButton,
    ]);
    const isServer = typeof window === 'undefined';
    const carouselItems = categoryId
        ? store.currentPortal?.getCategory(categoryId)?.items ?? []
        : items || [];
    // empty if no category has been selected and no items have been selected
    // and no title has been set.
    const isEmpty = !categoryId && !items && !content.slate;
    if (isEmpty && editData && editData.activeConfigId !== configId) {
        return (_jsx(PlaceHolder, { placeHolderName: "Configure Item Carousel" }));
    }
    return (_jsxs(Box, { sx: {
            backgroundColor: { backgroundColor },
            p: (`${padding}px`),
            ...(limitToPageWidth && {
                maxWidth: 1200,
                marginX: 'auto',
                width: '100%',
            }),
        }, children: [_jsx(WysiwygTitle, { showTitle: showTitle, editData: editData, content: content, configId: configId, disabled: disabled }), carouselItems.length === 0 && editData && (_jsx(PlaceHolder, { placeHolderName: "Carousel" })), carouselItems.length > 0 && (isServer ? (_jsx(CarouselSkeleton, {})) : (_jsx(Carousel, { responsive: responsive, ssr: false, children: carouselItems.slice(0, maxItemCount).map((item) => (_jsx(Box, { sx: {
                        margin: '4px 8px',
                    }, children: _jsx(VerticalMediaCard, { size: "large", config: cardConfig, item: item }) }, item.id))) })))] }));
};
ItemCarousel.defaultProps = {
    content: {
        slate: undefined,
        html: '<div>Title</div>',
    },
    categoryId: undefined,
    items: undefined,
    showTitle: true,
    showPrice: true,
    cardShowDescription: true,
    cardShowImage: true,
    cardShowInfoBar: true,
    cardShowInstructor: true,
    cardShowTitle: true,
    cardMaxLinesDescription: 4,
    cardShowActionButton: true,
    maxItemCount: 20,
    padding: 0,
    disabled: false,
};
// already does what we need
ItemCarousel.getData = getData;
ItemCarousel.meta = {
    group: 'display',
    name: 'Item Carousel',
    description: 'Displays items from a category or group in a carousel.',
    props: [
        {
            key: 'categoryId',
            name: 'Category',
            type: 'category',
            description: 'Category or group to display items from',
        },
        {
            key: 'showTitle',
            name: 'Show Title',
            type: 'boolean',
            description: 'Show the title above the carousel',
        },
        {
            key: 'content',
            name: 'Title',
            type: 'richtext',
            description: 'Title of carousel',
            inPlaceEdit: true,
        },
        {
            key: 'maxItemCount',
            name: 'Maximum Items',
            type: 'number',
            description: 'Maximum number of items to appear in carousel.',
        },
        {
            key: 'limitToPageWidth',
            name: 'Limit Width',
            type: 'boolean',
            description: 'Limit the width of the stack to the maximum page width',
            group: 'style',
        },
        {
            key: 'layout',
            name: 'Layout',
            type: 'layout',
            description: 'Layout properties such as spacing and background color.',
            group: 'style',
        },
        {
            key: 'cardShowTitle',
            name: 'Card Show Title',
            type: 'boolean',
            description: 'Show the title on each card',
        },
        {
            key: 'cardShowImage',
            name: 'Card Show Image',
            type: 'boolean',
            description: 'Show the image on each card',
        },
        {
            key: 'cardShowInfoBar',
            name: 'Card Show Info Bar',
            type: 'boolean',
            description: 'Show the info bar on each card',
        },
        {
            key: 'cardShowInstructor',
            name: 'Card Show Instructor',
            type: 'boolean',
            description: 'Show the instructor on each card',
        },
        {
            key: 'cardShowDescription',
            name: 'Card Show Description',
            type: 'boolean',
            description: 'Show the description on each card',
        },
        {
            key: 'cardShowActionButton',
            name: 'Card Show Action Button',
            type: 'boolean',
            description: 'Show the call-to-action button on each card',
        },
        {
            key: 'cardMaxLinesDescription',
            name: 'Card Max Lines Description',
            type: 'number',
            description: 'Maximum number of lines for the description on each card',
        },
        {
            key: 'showPrice',
            name: 'Show Price',
            type: 'boolean',
            description: 'Show the price on each card',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'ItemCarousel',
        props: {
            content: {
                slate: undefined,
                html: '<div>&nbsp;</div>',
            },
            showTitle: true,
            maxItemCount: 20,
            category: '@categoryId',
            limitToPageWidth: true,
            cardShowTitle: true,
            cardShowImage: true,
            cardShowInfoBar: true,
            cardShowInstructor: true,
            cardShowDescription: true,
            cardShowActionButton: true,
            cardMaxLinesDescription: 4,
            showPrice: true,
        },
    },
};
ItemCarousel.IpeWrapper = WysiwygTitle.IpeWrapper;
ItemCarousel.ipeControls = WysiwygTitle.ipeControls;
export default observer(ItemCarousel);
//# sourceMappingURL=ItemCarousel.js.map